import { useState } from 'react'
import { AUDIT_ACTIONS } from '@naya/shared'
import { AdminLayout } from '../components/AdminLayout.js'
import { useAuditLog, type AuditLogFilters } from '../hooks/useAuditLog.js'
import { useAdminsList } from '../hooks/useAdmins.js'
import { actionLabel } from '../actionLabels.js'
import { fmtDateTime } from '../format.js'
import type { AuditEntry } from '../types.js'

/** Builds the "who did it" label, including the impersonation form. */
function actorLabel(entry: AuditEntry): string {
  if (entry.actor_type === 'system') return 'system'
  if (entry.actor_type === 'impersonating_admin') {
    return `${entry.admin_email ?? 'admin'} → ${entry.acting_as_user_email ?? 'owner'}`
  }
  return entry.admin_email ?? 'admin'
}

interface FilterDraft {
  admin_id: string
  tenant_id: string
  action: string
  from: string
  to: string
  impersonation_only: boolean
}

const EMPTY_FILTERS: FilterDraft = {
  admin_id: '',
  tenant_id: '',
  action: '',
  from: '',
  to: '',
  impersonation_only: false,
}

export function AuditLog() {
  const adminsList = useAdminsList()

  // `draft` is what the form holds; `applied` is what's actually queried.
  // Cursor pagination resets whenever `applied` changes.
  const [draft, setDraft] = useState<FilterDraft>(EMPTY_FILTERS)
  const [applied, setApplied] = useState<FilterDraft>(EMPTY_FILTERS)
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [cursorStack, setCursorStack] = useState<string[]>([])
  const [expanded, setExpanded] = useState<string | null>(null)

  const filters: AuditLogFilters = {
    ...(applied.admin_id ? { admin_id: applied.admin_id } : {}),
    ...(applied.tenant_id ? { tenant_id: applied.tenant_id } : {}),
    ...(applied.action ? { action: applied.action } : {}),
    ...(applied.from
      ? { from: new Date(applied.from).toISOString() }
      : {}),
    ...(applied.to ? { to: new Date(applied.to).toISOString() } : {}),
    ...(applied.impersonation_only ? { impersonation_only: true } : {}),
    ...(cursor ? { cursor } : {}),
    limit: 50,
  }

  const query = useAuditLog(filters)

  function applyFilters() {
    setApplied(draft)
    setCursor(undefined)
    setCursorStack([])
  }

  function clearFilters() {
    setDraft(EMPTY_FILTERS)
    setApplied(EMPTY_FILTERS)
    setCursor(undefined)
    setCursorStack([])
  }

  function nextPage() {
    const next = query.data?.next_cursor
    if (!next) return
    setCursorStack((s) => [...s, cursor ?? ''])
    setCursor(next)
  }

  function prevPage() {
    setCursorStack((s) => {
      const copy = [...s]
      const prev = copy.pop()
      setCursor(prev === '' ? undefined : prev)
      return copy
    })
  }

  const events = query.data?.events ?? []

  return (
    <AdminLayout>
      <div className="admin-page-head">
        <h1>Audit log</h1>
      </div>

      {/* Filters */}
      <div className="admin-card" style={{ marginBottom: 16 }}>
        <div className="admin-filters" style={{ marginBottom: 12 }}>
          <select
            className="admin-select"
            aria-label="Filter by admin"
            value={draft.admin_id}
            onChange={(e) =>
              setDraft({ ...draft, admin_id: e.target.value })
            }
          >
            <option value="">All admins</option>
            {(adminsList.data?.admins ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.email}
              </option>
            ))}
          </select>

          <select
            className="admin-select"
            aria-label="Filter by action type"
            value={draft.action}
            onChange={(e) =>
              setDraft({ ...draft, action: e.target.value })
            }
          >
            <option value="">All actions</option>
            {AUDIT_ACTIONS.map((a) => (
              <option key={a} value={a}>
                {actionLabel(a)}
              </option>
            ))}
          </select>

          <input
            className="admin-input"
            placeholder="Tenant ID"
            aria-label="Filter by tenant ID"
            value={draft.tenant_id}
            onChange={(e) =>
              setDraft({ ...draft, tenant_id: e.target.value })
            }
          />
        </div>

        <div className="admin-filters" style={{ marginBottom: 12 }}>
          <input
            className="admin-input"
            type="date"
            aria-label="From date"
            value={draft.from}
            onChange={(e) => setDraft({ ...draft, from: e.target.value })}
          />
          <input
            className="admin-input"
            type="date"
            aria-label="To date"
            value={draft.to}
            onChange={(e) => setDraft({ ...draft, to: e.target.value })}
          />
          <label
            className="admin-toggle"
            style={{ flex: '1 1 200px' }}
          >
            <input
              type="checkbox"
              checked={draft.impersonation_only}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  impersonation_only: e.target.checked,
                })
              }
            />
            Impersonation events only
          </label>
        </div>

        <div className="admin-form-actions">
          <button
            type="button"
            className="admin-btn admin-btn-primary"
            onClick={applyFilters}
          >
            Apply filters
          </button>
          <button
            type="button"
            className="admin-btn"
            onClick={clearFilters}
          >
            Clear
          </button>
        </div>
      </div>

      {query.isLoading ? (
        <p className="admin-audit-meta">Loading audit log…</p>
      ) : query.isError ? (
        <div className="admin-error-state">
          <p>Couldn't load the audit log.</p>
          <button
            type="button"
            className="admin-btn"
            onClick={() => void query.refetch()}
          >
            Retry
          </button>
        </div>
      ) : events.length === 0 ? (
        <div className="admin-card">
          <div className="admin-empty">
            No audit events match these filters.
          </div>
        </div>
      ) : (
        <div className="admin-card">
          {events.map((entry) => {
            const isImpersonation =
              entry.actor_type === 'impersonating_admin' ||
              entry.acting_as_user_id !== null
            const isOpen = expanded === entry.id
            return (
              <div
                key={entry.id}
                className={`admin-audit-row admin-audit-row-clickable${
                  isImpersonation
                    ? ' admin-audit-row-impersonation'
                    : ''
                }`}
                onClick={() =>
                  setExpanded(isOpen ? null : entry.id)
                }
              >
                <div className="admin-audit-time">
                  {fmtDateTime(entry.created_at)}
                </div>
                <div>
                  <span className="admin-audit-action">
                    {actionLabel(entry.action)}
                  </span>
                  <span className="admin-audit-meta">
                    {' '}
                    · {actorLabel(entry)}
                  </span>
                  {entry.tenant_id && (
                    <span className="admin-audit-meta">
                      {' '}
                      · tenant {entry.tenant_id}
                    </span>
                  )}
                  {isOpen && (
                    <pre className="admin-json">
                      {JSON.stringify(
                        {
                          target_type: entry.target_type,
                          target_id: entry.target_id,
                          ip_address: entry.ip_address,
                          metadata: entry.metadata,
                        },
                        null,
                        2,
                      )}
                    </pre>
                  )}
                </div>
              </div>
            )
          })}

          <div
            className="admin-form-actions"
            style={{ marginTop: 14, justifyContent: 'space-between' }}
          >
            <button
              type="button"
              className="admin-btn admin-btn-sm"
              disabled={cursorStack.length === 0}
              onClick={prevPage}
            >
              ← Previous
            </button>
            <button
              type="button"
              className="admin-btn admin-btn-sm"
              disabled={!query.data?.next_cursor}
              onClick={nextPage}
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}
